import React, { FC } from 'react';

import { Box, HStack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import {
  Select,
  publishedAtOptions,
  OutlinedIconButton,
} from 'components/DataEntry';
import { ClockIcon, ViewAllIcon } from 'components/Icons';
import { SyncRestartIcon } from 'components/Icons/ReactCustomIcons/SyncRestartIcon';
import { useDataDashboardContext } from 'containers/Visibility/Data/Components/hooks/context';
import { selectDataDashboard } from 'containers/Visibility/Data/selectors';

interface Props {
  isExpanded?: boolean;
  setIsExpanded?(val: boolean);
  onOpenSyncLabels?: () => void;
  filterTab?: string;
  showActions?: boolean;
  showDataTypeFilter?: boolean;
  filterScope?: 'dashboard' | 'drawer';
}

export const DataSecurityFilters: FC<Props> = props => {
  const {
    isExpanded,
    setIsExpanded,
    onOpenSyncLabels,
    filterTab,
    showActions = true,
    showDataTypeFilter = true,
    filterScope = 'dashboard',
  } = props;
  const { selectedTab, dataClassifiers } = useSelector(selectDataDashboard);
  // Detail drawers can reuse the Sensitive Sources filter layout without changing the selected tab.
  const activeFilterTab = filterTab ?? selectedTab;
  const {
    time: dashboardTime,
    regionOptions: dashboardRegionOptions,
    accountOptions: dashboardAccountOptions,
    sourceTypeOptions: dashboardSourceTypeOptions,
    resourceTypeOptions: dashboardResourceTypeOptions,
    selectedSourceType: dashboardSelectedSourceType,
    selectedResourceType: dashboardSelectedResourceType,
    selectedAccount: dashboardSelectedAccount,
    selectedRegion: dashboardSelectedRegion,
    labelOptions: dashboardLabelOptions,
    cloudOptions: dashboardCloudOptions,
    selectedCloud: dashboardSelectedCloud,
    selectedLabel: dashboardSelectedLabel,
    selectedSensitivity: dashboardSelectedSensitivity,
    sensitivityOptions: dashboardSensitivityOptions,
    updateValue,
    selectedDataType: dashboardSelectedDataType,
    dataTypeOptions: dashboardDataTypeOptions,
    drawerFilters,
    updateDrawerValue,
  } = useDataDashboardContext();
  const filters =
    filterScope === 'drawer'
      ? drawerFilters
      : {
          time: dashboardTime,
          regionOptions: dashboardRegionOptions,
          accountOptions: dashboardAccountOptions,
          sourceTypeOptions: dashboardSourceTypeOptions,
          resourceTypeOptions: dashboardResourceTypeOptions,
          selectedSourceType: dashboardSelectedSourceType,
          selectedResourceType: dashboardSelectedResourceType,
          selectedAccount: dashboardSelectedAccount,
          selectedRegion: dashboardSelectedRegion,
          labelOptions: dashboardLabelOptions,
          cloudOptions: dashboardCloudOptions,
          selectedCloud: dashboardSelectedCloud,
          selectedLabel: dashboardSelectedLabel,
          selectedSensitivity: dashboardSelectedSensitivity,
          selectedDataType: dashboardSelectedDataType,
          sensitivityOptions: dashboardSensitivityOptions,
          dataTypeOptions: dashboardDataTypeOptions,
        };
  const updateFilterValue =
    filterScope === 'drawer' ? updateDrawerValue : updateValue;
  const {
    time,
    regionOptions,
    accountOptions,
    sourceTypeOptions,
    resourceTypeOptions,
    selectedSourceType,
    selectedResourceType,
    selectedAccount,
    selectedRegion,
    labelOptions,
    cloudOptions,
    selectedCloud,
    selectedLabel,
    selectedSensitivity,
    sensitivityOptions,
    selectedDataType,
    dataTypeOptions,
  } = filters;

  const styles = {
    menu: provided => ({ ...provided, width: 'max-content', minWidth: '100%' }),
  };

  return activeFilterTab === 'all' ? (
    <Box />
  ) : (
    <Box w="full">
      <HStack pr={isExpanded ? 6 : 0} w="full" justify="space-between">
        <HStack spacing={4}>
          <HStack>
            <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
            <Box>
              <Select
                options={cloudOptions}
                value={selectedCloud}
                onChange={val => {
                  updateFilterValue('selectedCloud', val);
                  if (!!val?.value)
                    updateFilterValue('selectedAccount', accountOptions[0]);
                }}
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                showIconInValueContainer
                styles={styles}
                isAFilter
                leftMessage="Cloud"
                showUndefinedValueLabel={false}
                showClearIndicator
              />
            </Box>
            {activeFilterTab === 'DataSources' && (
              <Box>
                <Select
                  options={accountOptions}
                  value={selectedAccount}
                  onChange={val => {
                    updateFilterValue('selectedAccount', val);
                  }}
                  menuPortalTarget={document.body}
                  menuPosition={'fixed'}
                  showIconInValueContainer
                  styles={styles}
                  isAFilter
                  leftMessage="Account"
                  showUndefinedValueLabel={false}
                  showClearIndicator
                />
              </Box>
            )}
            {activeFilterTab === 'Identities' && (
              <Box>
                <Select
                  options={resourceTypeOptions}
                  value={selectedResourceType}
                  onChange={val =>
                    updateFilterValue('selectedResourceType', val)
                  }
                  menuPortalTarget={document.body}
                  menuPosition={'fixed'}
                  showIconInValueContainer
                  styles={styles}
                  isAFilter
                  leftMessage="Resource Type"
                  showUndefinedValueLabel={false}
                  showClearIndicator
                />
              </Box>
            )}
            {activeFilterTab === 'DataSources' && (
              <Box>
                <Select
                  options={regionOptions}
                  value={selectedRegion}
                  onChange={val => updateFilterValue('selectedRegion', val)}
                  menuPortalTarget={document.body}
                  menuPosition={'fixed'}
                  showIconInValueContainer
                  styles={styles}
                  isAFilter
                  leftMessage="Region"
                  showClearIndicator
                  showUndefinedValueLabel={false}
                />
              </Box>
            )}
            {activeFilterTab === 'DataSources' && (
              <Box>
                <Select
                  options={sourceTypeOptions}
                  value={selectedSourceType}
                  onChange={val => updateFilterValue('selectedSourceType', val)}
                  menuPortalTarget={document.body}
                  menuPosition={'fixed'}
                  showIconInValueContainer
                  styles={styles}
                  isAFilter
                  leftMessage="Source Type"
                  showUndefinedValueLabel={false}
                  showClearIndicator
                />
              </Box>
            )}
            {activeFilterTab === 'DataTypes' && (
              <Box>
                <Select
                  options={sensitivityOptions}
                  value={selectedSensitivity}
                  onChange={val =>
                    updateFilterValue('selectedSensitivity', val)
                  }
                  menuPortalTarget={document.body}
                  menuPosition={'fixed'}
                  showIconInValueContainer
                  styles={styles}
                  isAFilter
                  leftMessage="Sensitivity"
                  showUndefinedValueLabel={false}
                  showClearIndicator
                />
              </Box>
            )}
            <Box>
              <Select
                options={labelOptions}
                value={selectedLabel}
                onChange={val => updateFilterValue('selectedLabel', val)}
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                showIconInValueContainer
                styles={styles}
                isAFilter
                leftMessage="Label"
                showUndefinedValueLabel={false}
                showClearIndicator
              />
            </Box>
            {showDataTypeFilter &&
              (activeFilterTab === 'DataSources' ||
                activeFilterTab === 'Identities') && (
                <Box>
                  <Select
                    options={dataTypeOptions}
                    value={selectedDataType}
                    onChange={val => updateFilterValue('selectedDataType', val)}
                    menuPortalTarget={document.body}
                    menuPosition={'fixed'}
                    showIconInValueContainer
                    styles={styles}
                    isAFilter
                    isLoading={dataClassifiers.isLoading}
                    leftMessage="Data Type"
                    showUndefinedValueLabel={false}
                    showClearIndicator
                  />
                </Box>
              )}
            <Box>
              <Select
                options={publishedAtOptions}
                value={time}
                onChange={val => updateFilterValue('time', val)}
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                showIconInValueContainer
                valueContainerIcon={<ClockIcon />}
                styles={styles}
                isAFilter
                leftMessage="Timeline"
              />
            </Box>
          </HStack>
        </HStack>
        {showActions && (
          <HStack>
            <OutlinedIconButton
              aria-label="Sync Labels"
              onClick={() => onOpenSyncLabels?.()}
              tooltip="Sync Labels"
              showGrayBg
              leftIcon={<SyncRestartIcon />}
            >
              Sync Labels
            </OutlinedIconButton>
            {!isExpanded && (
              <OutlinedIconButton
                aria-label="expand"
                onClick={() => setIsExpanded?.(true)}
                tooltip="View all"
                showGrayBg
                leftIcon={<ViewAllIcon p={1} />}
              >
                Show full page
              </OutlinedIconButton>
            )}
            {/*<DownloadIconButton
            aria-label="download-vulnerabilities"
            //isLoading={download.isLoading}
            tooltip="Download Report"
            onClick={() => {}}
          />*/}
          </HStack>
        )}
      </HStack>
    </Box>
  );
};
